import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Coins, Network, Bug } from "lucide-react";

const SOLANA_GRADIENT =
    "linear-gradient(95deg, #9945FF 0%, #DC1FFF 35%, #FF6FA1 60%, #14F195 100%)";

const TEASERS = [
    { icon: Coins, label: "Protocol Based Rewards" },
    { icon: Network, label: "Delegating Stake" },
    { icon: Bug, label: "Security Researchers" },
];

/**
 * Landing-hub teaser section linking out to the full /solana portal.
 * Wraps the Solana brand gradient (pink/purple/cyan) over the EIGOO
 * obsidian canvas so the section feels like a portal — not a clash.
 */
export default function SolanaPortalSection() {
    return (
        <section
            data-testid="solana-portal-section"
            className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-20"
        >
            {/* Aurora wash */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
                <div
                    className="absolute top-0 left-[10%] w-[40%] h-[60%] rounded-full blur-[120px] opacity-30"
                    style={{ background: "radial-gradient(circle, #9945FF 0%, transparent 70%)" }}
                />
                <div
                    className="absolute bottom-0 right-[5%] w-[40%] h-[60%] rounded-full blur-[120px] opacity-25"
                    style={{ background: "radial-gradient(circle, #14F195 0%, transparent 70%)" }}
                />
            </div>

            <div
                className="relative rounded-3xl p-1"
                style={{ background: SOLANA_GRADIENT }}
            >
                <div
                    className="rounded-[calc(1.5rem-4px)] p-9 lg:p-12 grid lg:grid-cols-12 gap-8 items-start"
                    style={{ background: "hsl(0 0% 4%)" }}
                >
                    <div className="lg:col-span-7">
                        <div
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.45em] font-mono mb-6"
                            style={{
                                color: "#FF6FA1",
                                border: "1px solid rgba(255,111,161,0.4)",
                                background: "rgba(255,255,255,0.03)",
                            }}
                            data-testid="solana-section-eyebrow"
                        >
                            <span
                                className="h-2 w-2 rounded-full animate-pulse"
                                style={{ background: "#14F195", boxShadow: "0 0 12px #14F195" }}
                            />
                            Solana Portal · DSRE Charter
                        </div>

                        <h2
                            className="font-heading tracking-tighter leading-[0.95] text-white"
                            style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}
                            data-testid="solana-section-headline"
                        >
                            A Distributed{" "}
                            <span
                                className="bg-clip-text text-transparent"
                                style={{ backgroundImage: SOLANA_GRADIENT }}
                            >
                                Security Research Entity
                            </span>{" "}
                            for the Solana Network.
                        </h2>

                        <p
                            className="mt-6 text-lg text-white/75 leading-relaxed max-w-xl"
                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}
                        >
                            "We don't speculate on chains. We armor them — and earn protocol-based
                            rewards while doing it."
                        </p>

                        <div className="mt-7 flex flex-wrap gap-3">
                            <Link
                                to="/solana"
                                data-testid="solana-section-enter"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-black hover:scale-[1.02] transition-transform"
                                style={{ background: SOLANA_GRADIENT }}
                            >
                                Enter the Solana Portal <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/foundry/control"
                                data-testid="solana-section-research"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-white/15 text-white hover:border-[#14F195] hover:text-[#14F195] transition-colors"
                            >
                                <Bug size={15} /> File research brief
                            </Link>
                        </div>
                    </div>

                    {/* Three pillar teasers */}
                    <div className="lg:col-span-5 space-y-3" data-testid="solana-section-pillars">
                        {TEASERS.map((t) => {
                            const Icon = t.icon;
                            return (
                                <div
                                    key={t.label}
                                    className="flex items-center gap-4 p-4 rounded-xl border border-white/[0.08] bg-white/[0.02] hover:border-white/20 transition-colors"
                                >
                                    <span
                                        className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                                        style={{
                                            background: "rgba(255,255,255,0.03)",
                                            border: "1px solid rgba(20,241,149,0.4)",
                                        }}
                                    >
                                        <Icon size={16} style={{ color: "#14F195" }} />
                                    </span>
                                    <div className="flex-1">
                                        <div className="font-heading text-xl tracking-tight text-white">
                                            {t.label}
                                        </div>
                                    </div>
                                    <ArrowRight size={14} className="text-white/35" />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
